import EditIcon from "@mui/icons-material/Edit"
import {
	Box,
	Card,
	CardActions,
	CardContent,
	Grid,
	IconButton,
	Theme,
	Tooltip,
	Typography,
} from "@mui/material"
import { styled } from "@mui/material/styles"
import { AxiosError, AxiosPromise } from "axios"
import { format, parseISO } from "date-fns"
import { toZonedTime } from "date-fns-tz"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"

import {
	CampaignsState,
	getApprovalRules,
} from "../../../store/actions/campaigns"
import { openDrawer } from "../../../store/campaigns/campaign-edit-slice"
import CampaignDisabled from "../disabled"

const CampaignCard = ({
	bankId,
	campaignInfo,
	userTimeZone,
}: {
	campaignInfo: CampaignsState
	bankId: string
	userTimeZone: any
}) => {
	const { t } = useTranslation()
	const dispatch = useDispatch()
	const campaignCreationDate: Date | undefined = campaignInfo?.creationDate
		? parseISO(campaignInfo.creationDate)
		: undefined
	const [isFlipped, setIsFlipped] = useState<boolean>(false)
	const [isLoadingApproval, setIsLoadingApproval] = useState<boolean>(false)

	const zonedDate = campaignCreationDate
		? toZonedTime(campaignCreationDate, userTimeZone)
		: undefined
	const formattedDate = zonedDate && format(zonedDate, "dd/MM/yyyy")
	const formattedDateTime =
		zonedDate && format(zonedDate, "dd/MM/yyyy HH:mm:ss")

	const handleEditCampaign = (
		campaign: CampaignsState,
		tabIndex: number | undefined
	) => {
		dispatch(
			openDrawer({
				isEditMode: true,
				campaign: campaign,
				tabIndex: tabIndex ?? 0,
			})
		)
	}

	const Strikethrough = styled("div")(() => ({
		display: "inline-block",
		[`&.strikethrough`]: {
			background:
				"linear-gradient(to left top, transparent 47.75%, #333 49.5%, #333 50.5%, transparent 52.25%)",
		},
		[`&.strikethrough > svg`]: {
			position: "relative",
			filter: "grayscale(1)",
			opacity: 0.7,
		},
		[`> svg`]: {
			width: "30px",
			height: "30px",
			verticalAlign: "middle",
		},
	}))

	const PaydoCard = styled(Card)(() => ({
		width: "300px",
		margin: "16px",
		boxShadow:
			"0px 2px 4px -1px rgba(0,0,0,0.2),0px 4px 5px 0px rgba(0,0,0,0.14),0px 1px 10px 0px rgba(0,0,0,0.12)",
		[`& .deleted`]: {
			backgroundColor: "rgba(255, 0, 0, 0.1)",
		},
		[`& .disabled`]: {
			backgroundColor: "#F5F5F5",
		},
	}))

	const CardHeader = styled(Box)(({ theme }: { theme: Theme }) => ({
		display: "flex",
		justifyContent: "space-between",
		color: theme.palette.primary.main,
		marginBottom: "4px",
		alignItems: "flex-start",
		[`& .title`]: {
			color: theme.palette.primary.main,
		},
	}))

	const CardInfo = styled(Typography)(() => ({
		color: "#555",
		marginBottom: "8px",
	}))

	const CardHighlightedInfo = styled(Box)(({ theme }: { theme: Theme }) => ({
		display: "flex",
		justifyContent: "space-between",
		color: theme.palette.primary.main,
		alignItems: "center",
	}))

	const CardFooter = styled(CardActions)(() => ({
		backgroundColor: "#F0F0F0",
		padding: "8px",
		borderRadius: "0 0 8px 8px",
		display: "flex",
		justifyContent: "space-between",
	}))

	const GridContainer = styled(Grid)(() => ({
		display: "flex",
		alignItems: "space-beetween",
		width: "100%",
	}))

	const TextEllipsis = styled(Grid)(() => ({
		overflow: "hidden",
		whiteSpace: "nowrap",
		textOverflow: "ellipsis",
		flexGrow: 1,
		marginRight: 8,
	}))

	const GridDate = styled(Grid)(() => ({
		maxWidth: "85px",
		textAlign: "right",
	}))

	const base64Image = campaignInfo?.campaignLogo ?? undefined
	const campaignImgSrc = base64Image && atob(base64Image)

	const handleFlip = () => {
		if (campaignInfo?.idLogo) {
			setIsLoadingApproval(true)
			dispatch(getApprovalRules({ campaignIdLogo: campaignInfo?.idLogo }))
				.then((response: AxiosPromise) => {
					setIsLoadingApproval(false)
				})
				.catch((error: AxiosError<string>) => {
					setIsLoadingApproval(false)
				})
			setIsFlipped(!isFlipped)
		}
	}

	return (
		<PaydoCard
			className={
				campaignInfo?.deleted
					? "deleted"
					: campaignInfo?.enabled
						? "default"
						: "disabled"
			}
		>
			<CardContent>
				<CardHeader>
					<GridContainer container wrap="nowrap">
						<Grid item xs zeroMinWidth>
							<CardInfo className={`title `} variant="body1">
								<Tooltip title={campaignInfo.campaignName}>
									<TextEllipsis>{campaignInfo.campaignName}</TextEllipsis>
								</Tooltip>{" "}
							</CardInfo>
						</Grid>
						<GridDate item>
							<div style={{ marginTop: "-4px" }}>
								<img
									alt={t("logo.bank", { ns: "common" })}
									style={{
										maxWidth: "100%",
										height: "fit-content",
										verticalAlign: "middle",
									}}
									src={campaignImgSrc}
								/>
							</div>
							<Typography
								component={"div"}
								variant="subtitle2"
								color="textSecondary"
							>
								<Tooltip title={`creato il ${formattedDateTime}`}>
									<span>{formattedDate}</span>
								</Tooltip>
							</Typography>
						</GridDate>
					</GridContainer>
				</CardHeader>
				<div className={`flip-card ${isFlipped ? "flipped" : ""}`}>
					<div className="flip-card-inner">
						<Grid
							className="flip-card-front"
							sx={{ display: isFlipped ? "none" : "block" }}
							container
							gap={"8px"}
							flexWrap={"wrap"}
							justifyContent={"space-between"}
							alignItems={"center"}
						>
							<Grid item>
								<CardInfo variant="body2">
									<b>{t(`card.corporate`, { ns: "campaign" })}:</b>{" "}
									{campaignInfo?.corporateName}
								</CardInfo>
							</Grid>

							<CardHighlightedInfo>
								<CardInfo sx={{ textAlign: "right" }}>
									{" "}
									<Typography component="span" variant="caption">
										<b>{t(`card.type`, { ns: "campaign" })}: </b>
									</Typography>
									{campaignInfo.authorizationType}{" "}
								</CardInfo>
							</CardHighlightedInfo>
						</Grid>
						<Grid
							className="flip-card-back"
							sx={{ display: isFlipped ? "block" : "none" }}
							container
							gap={"8px"}
							flexWrap={"wrap"}
							justifyContent={"space-between"}
							alignItems={"center"}
						>
							{t("permissions.title", { ns: "user" })}:
							{/*userInfo?.permissions?.map((el: PermissionState) => (
								<Grid key={`permission_${el.permissionName}`} container>
									<Grid item sx={{ marginRight: "10px" }}>
										{el.enabled ? (
											<CheckCircleIcon
												style={{
													color: "green",
													verticalAlign: "middle",
													fontSize: "15px",
												}}
											/>
										) : (
											<DoNotDisturbOnIcon
												style={{
													color: "red",
													verticalAlign: "middle",
													fontSize: "15px",
												}}
											/>
										)}{" "}
									</Grid>
									<Grid
										item
										flexGrow={1}
										sx={{ wordWrap: "break-word", width: "calc(100% - 50px)" }}
									>
										<span style={{ fontSize: "12px" }}>
											{t(`permissions.name.${el.permissionName}`, {
												ns: "user",
											})}
										</span>{" "}
									</Grid>
								</Grid>
							))*/}
						</Grid>
					</div>
				</div>
			</CardContent>
			<CardFooter>
				<Box>
					{/** 
					<IconButton color="primary" onClick={() => handleFlip()}>
						<Tooltip
							title={t(`card.tooltip.${isFlipped ? "info" : "approvers"}`, {
								ns: "campaign",
							})}
						>
							{isFlipped ? <CampaignIcon /> : <HowToRegIcon />}
						</Tooltip>
					</IconButton>
					*/}
					<IconButton
						color="primary"
						onClick={() => {
							handleEditCampaign(campaignInfo, 1)
						}}
					>
						<Tooltip title={t(`buttons.tooltip.modify`, { ns: "campaign" })}>
							<EditIcon />
						</Tooltip>
					</IconButton>
				</Box>
				<Box sx={{ display: "flex", gap: 1 }}>
					<CampaignDisabled
						bankId={bankId}
						campaignId={campaignInfo.id}
						campaignName={campaignInfo?.campaignName ?? "N/A"}
						enabled={campaignInfo.enabled}
					/>
				</Box>
			</CardFooter>
		</PaydoCard>
	)
}

export default CampaignCard
